import { FunctionComponent, useState } from 'react';
import { Button } from '@chakra-ui/react';

import { apolloClient } from 'common/graphql/clients';
import Heading from 'components/_core/Heading';
import useToast from 'components/ToastProvider/useToast';
import { GetBeneficiariesDataDocument } from 'features/Onboarding/modules/Beneficiaries/graphql/queries/getBeneficiariesData.gql';
import { MultiKycUnderReviewDataDocument } from 'features/Onboarding/pages/UnderReview/components/MultiKycContent/graphql/queries/multiKycUnderReviewData.gql';

import useCompanyContext from '../../../../../../common/hooks/useCompanyContext';
import {
  BeneficiaryFormWithEmail,
  formatCompanyBeneficiaryToBeneficiaryFormWithEmailInput,
} from '../../../../components/BeneficiaryForm';
import { BeneficiaryFormOutput } from '../../../BeneficiaryForm/beneficiarySchema';
import { CreateBeneficiaryDocument } from '../../graphql/mutations/createBeneficiary.gql';
import { SendCompanyInvitationDocument } from '../../graphql/mutations/sendBeneficiaryInvitation.gql';
import { Beneficiary } from '../../types';
import locales from './locales';

type CreateBeneficiaryProps = {
  onCancel: () => void;
  onBeneficiaryCreated: (beneficiary: Beneficiary) => void;
  currentBeneficiariesEmails: Set<string>;
};

const CreateBeneficiary: FunctionComponent<CreateBeneficiaryProps> = ({
  currentBeneficiariesEmails,
  onBeneficiaryCreated,
  onCancel,
}) => {
  const { companyProfileId } = useCompanyContext();
  const { showToast } = useToast();

  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (formValues: BeneficiaryFormOutput) => {
    if (!companyProfileId) {
      throw new Error('companyProfileId should be defined');
    }

    setIsSending(true);

    const { data } = await apolloClient.mutate({
      awaitRefetchQueries: true,
      mutation: CreateBeneficiaryDocument,
      refetchQueries: [
        GetBeneficiariesDataDocument,
        MultiKycUnderReviewDataDocument,
      ],
      variables: {
        input: {
          ...formValues,
          companyProfileId,
        },
      },
    });

    if (!data) {
      throw new Error('createBeneficiary mutation failed');
    }

    const { invitation } = data.createPhysicalPersonLegalBeneficiary;

    if (!invitation) {
      throw new Error(
        'Unexpected undefined CompanyLegalBeneficiary.invitation field',
      );
    }

    await apolloClient.mutate({
      mutation: SendCompanyInvitationDocument,
      variables: {
        input: {
          companyInvitationId: invitation.companyInvitationId,
          companyProfileId,
        },
      },
    });

    showToast({
      message: `${locales.formatString(
        formValues.gender === 'male'
          ? locales.addedMaleBeneficiary
          : locales.addedFemaleBeneficiary,
        {
          name: `${formValues.firstName} ${formValues.lastName}`,
        },
      )}`,
      type: 'success',
    });

    setIsSending(false);
    onBeneficiaryCreated(data.createPhysicalPersonLegalBeneficiary);
  };

  return (
    <>
      <Heading marginBottom="space-48" size="lg">
        {locales.title}
      </Heading>
      <BeneficiaryFormWithEmail
        beneficiary={formatCompanyBeneficiaryToBeneficiaryFormWithEmailInput(
          null,
        )}
        currentBeneficiariesEmails={currentBeneficiariesEmails}
        primaryAction={{
          isSending,
          onSubmit: handleSubmit,
          title: locales.cta,
        }}
        secondaryAction={
          <Button onClick={onCancel} variant="secondary">
            {locales.cancel}
          </Button>
        }
      />
    </>
  );
};

export default CreateBeneficiary;
